// X160 (Grant 2026-10-27: "There's no way to access league settings anymore… a tabbed approach would be better so we
// have a main page for admin of each competition type and then split the tabs"): the pure words and routes behind
// the shared per-competition admin shell (components/admin/competitions/CompetitionAdminShell.vue) and the ⋮ menu on
// the Competitions list's rows.
// X162 (Grant 2026-10-27: "switching tabs, there is so much layout shift but nothing is actually changing above the
// tabs… why not use a redirect from the old link and use query params like the rest of the tabs in the system?"):
// ONE page per competition (its index route) and the tab in `?tab=` (router.replace, no navigation), the way Club
// settings / Bookings / Memberships & passes work. The first tab carries no `?tab=`. The old sub-page addresses
// (/settings, /payments, /players, /teams) 301 to the page with `?tab=` (oldSubPageRedirect, the X144 middleware).
// Payments only shows when the competition charges. Nothing here talks to the API.
import {formatExplainers} from '~/utils/formatExplainers';
import {competitionIcon, competitionRoute} from '~/utils/clubDashboard';
import {emailFilters, statusChip} from '~/utils/competitionsPage';
import {isPaidRule} from '~/utils/entryRequirements';

export const COMPETITION_KINDS = Object.freeze(['league', 'ladder', 'tournament', 'americano', 'meet']);

/** The route param each format's admin pages take. */
export const ID_PARAM = Object.freeze({
  league: 'leagueId', ladder: 'ladderId', tournament: 'tournamentId', americano: 'americanoId', meet: 'meetId',
});

/** InviteEntityReferenceType (API InviteLink.cs): what people join; a cycle for box and americano leagues. */
export const JOIN_REFERENCE_TYPE = Object.freeze({ladder: 1, league: 2, tournament: 4, meet: 5, americano: 6});

const TAB_ICONS = Object.freeze({
  cycles: 'mdi-autorenew',
  players: 'mdi-account-group-outline',
  draw: 'mdi-tournament',
  teams: 'mdi-account-multiple-outline',
  dates: 'mdi-calendar-outline',
  settings: 'mdi-cog-outline',
  payments: 'mdi-credit-card-outline',
});

/** Each format's ONE admin page (X162): the competition's index route; the tab rides in its query. */
export const MANAGE_ROUTE = Object.freeze({
  league: 'ClubLeagueManage', ladder: 'ClubLadderManage', tournament: 'ClubTournamentManage',
  americano: 'ClubAmericanoManage', meet: 'ClubMeetManage',
});

/**
 * The tabs per format, in order: the value and the words. Grant (2026-10-27): no Players tab for box leagues and
 * americanos ("all players are managed within a cycle"). A ladder's Players tab (positions, drag to reorder) is its
 * main tab. Meets have no players admin tab (players are added on the Dates tab). The first tab is the page's
 * default: it carries no `?tab=`.
 */
const TABS = Object.freeze({
  league: [
    {value: 'cycles', title: 'Cycles & divisions'},
    {value: 'settings', title: 'Settings'},
    {value: 'payments', title: 'Payments'},
  ],
  ladder: [
    {value: 'players', title: 'Players'},
    {value: 'settings', title: 'Settings'},
    {value: 'payments', title: 'Payments'},
  ],
  // X162 (Grant: "you can't make the draw without teams… ensure teams is labelled players for singles tournaments"):
  // Teams (Players when singles) first, then Draw; the page opens on Draw once the draw exists (defaultCompetitionTab).
  tournament: [
    {value: 'teams', title: 'Teams', singlesTitle: 'Players'},
    {value: 'draw', title: 'Draw'},
    {value: 'settings', title: 'Settings'},
    {value: 'payments', title: 'Payments'},
  ],
  americano: [
    {value: 'cycles', title: 'Cycles & divisions'},
    {value: 'settings', title: 'Settings'},
    {value: 'payments', title: 'Payments'},
  ],
  meet: [
    {value: 'dates', title: 'Dates'},
    {value: 'settings', title: 'Settings'},
    {value: 'payments', title: 'Payments'},
  ],
});

/** The query keys a tab owns (its own state in the address); they leave when the admin switches tab. */
export const TAB_QUERY_KEYS = Object.freeze(['section', 'cycleId', 'view', 'page']);

/** A singles tournament (one player per team) says "Players" where a doubles one says "Teams". */
export function isSinglesTournament(item) {
  if (!item) return false; // unknown: say Teams
  return (Number(item.playersPerTeam ?? 1) || 1) <= 1;
}

/**
 * The tab a format's page opens on without `?tab=`: the first tab, except a tournament whose draw exists opens on
 * Draw (the order stays Teams → Draw). Without the competition (a link from elsewhere) a tournament's default is
 * unknown, so links to its tabs always say which.
 */
export function defaultCompetitionTab(kind, item = undefined) {
  if (kind === 'tournament') {
    if (item === undefined) return null;
    return (item?.stages?.length ?? 0) > 0 ? 'draw' : 'teams';
  }
  return TABS[kind]?.[0]?.value ?? null;
}

/**
 * The shell's tabs for one competition: TlPillTabs items (button tabs: the page keeps the value in `?tab=`).
 * Payments only when the competition charges (`charges`), the same rule the old list pages used (a fee now, or fees
 * collected before).
 */
export function competitionTabs(kind, {charges = false, item = null} = {}) {
  const tabs = TABS[kind] ?? [];
  const singles = kind === 'tournament' && isSinglesTournament(item);
  return tabs
      .filter(t => t.value !== 'payments' || charges)
      .map(t => ({value: t.value, title: singles && t.singlesTitle ? t.singlesTitle : t.title, icon: TAB_ICONS[t.value]}));
}

/**
 * The tab the address asks for: `?tab=` when the format has it (Payments only when it charges), else the first.
 * A bad or missing value never breaks the page.
 */
export function pickCompetitionTab(kind, query = {}, {charges = false, item = null} = {}) {
  const wanted = query?.tab == null ? null : String(Array.isArray(query.tab) ? query.tab[0] : query.tab);
  const tabs = competitionTabs(kind, {charges, item});
  return tabs.find(t => t.value === wanted)?.value ?? defaultCompetitionTab(kind, item) ?? tabs[0]?.value ?? null;
}

/** The address of a competition's page on one tab: no `?tab=` for the first tab; other query keys ride along. */
export function competitionTabRoute(kind, clubId, id, tab = null, query = null, item = undefined) {
  const name = MANAGE_ROUTE[kind];
  if (!name) return null;
  const q = {...(query ?? {})};
  delete q.tab;
  if (tab && tab !== defaultCompetitionTab(kind, item) && (TABS[kind] ?? []).some(t => t.value === tab)) q.tab = tab;
  return Object.keys(q).length ? {name, params: {clubId, [ID_PARAM[kind]]: id}, query: q} : {name, params: {clubId, [ID_PARAM[kind]]: id}};
}

/**
 * The route query after a tab switch on the page: the tab (none for the first), every other key kept except the ones
 * the tabs own (TAB_QUERY_KEYS: a section deep link, the payments cycle, the dates view/page).
 */
export function tabQuery(kind, existing = {}, tab = null, item = undefined) {
  const q = {...(existing ?? {})};
  for (const k of TAB_QUERY_KEYS) delete q[k];
  delete q.tab;
  if (tab && tab !== defaultCompetitionTab(kind, item)) q.tab = tab;
  return q;
}

/** The Settings / Payments addresses for a row of the Competitions list (its ⋮ menu). */
export function settingsRoute(kind, clubId, id) {
  return competitionTabRoute(kind, clubId, id, 'settings');
}
export function paymentsRoute(kind, clubId, id) {
  return competitionTabRoute(kind, clubId, id, 'payments');
}

/** The old per-competition sub-page paths (X160's link tabs) → the tab they are now. */
const OLD_SUB_PAGES = Object.freeze({
  leagues: {settings: 'settings', payments: 'payments'},
  ladders: {players: 'players', settings: 'settings', payments: 'payments'},
  tournaments: {teams: 'teams', settings: 'settings', payments: 'payments'},
  americanos: {settings: 'settings', payments: 'payments'},
  meets: {settings: 'settings', payments: 'payments'},
});
const OLD_PATH_KIND = Object.freeze({leagues: 'league', ladders: 'ladder', tournaments: 'tournament', americanos: 'americano', meets: 'meet'});

/**
 * An old sub-page address (/admin/clubs/{c}/leagues/{id}/settings, /ladders/{id}/players, /tournaments/{id}/teams,
 * …/payments) → the competition's page with `?tab=` (none for the first tab), every other query key kept; null when
 * the path is not one. A meet date's page (/meets/{id}/events/{occurrenceId}) is its own page and never matches.
 */
export function oldSubPageRedirect(path, query = {}) {
  const m = String(path ?? '').match(/^\/admin\/clubs\/([^/]+)\/(leagues|ladders|tournaments|americanos|meets)\/([^/]+)\/(settings|payments|players|teams)\/?$/);
  if (!m) return null;
  const [, clubId, old, id, sub] = m;
  const tab = OLD_SUB_PAGES[old]?.[sub];
  if (!tab) return null;
  const kind = OLD_PATH_KIND[old];
  const rest = {...(query ?? {})};
  delete rest.tab;
  if (tab !== defaultCompetitionTab(kind)) rest.tab = tab;
  return {path: `/admin/clubs/${clubId}/${old}/${id}`, query: rest};
}

/**
 * Breadcrumbs for every per-competition admin page: Dashboard › Competitions (filtered to the format) › name. The
 * tab is never a crumb (the pill row says where you are).
 */
export function competitionCrumbs(kind, clubId, name) {
  return [
    {title: 'Dashboard', to: {name: 'ClubDashboard', params: {clubId}}},
    {title: 'Competitions', to: {name: 'ClubCompetitions', params: {clubId}, query: {format: kind}}},
    {title: name || formatExplainers[kind]?.singular || 'Competition', disabled: true},
  ];
}

/** The competition's public page (View public page opens it in a new tab). */
export function publicRoute(kind, id) {
  const names = {league: 'LeagueDetail', ladder: 'LadderDetail', tournament: 'TournamentDetail', americano: 'AmericanoDetail', meet: 'MeetDetail'};
  return names[kind] && id ? {name: names[kind], params: {[ID_PARAM[kind]]: id}} : null;
}

/** The latest cycle of a box or americano league (archived ones skipped): the one Invite settings is about by default. */
export function latestCycle(item) {
  const cycles = (item?.cycles ?? []).filter(c => c && !c.archivedAt);
  if (!cycles.length) return null;
  return cycles.slice().sort((a, b) => new Date(b.startDate ?? 0) - new Date(a.startDate ?? 0))[0];
}

/**
 * What the Invite settings dialog is about: the ladder, tournament or meet itself; a box or americano league's cycle
 * (the one in view, else the latest). Null when there is nothing to invite to yet (a league without a cycle).
 */
export function joinReference(kind, item, cycle = null) {
  if (!item?.id) return null;
  const type = JOIN_REFERENCE_TYPE[kind];
  if (type == null) return null;
  if (kind === 'league' || kind === 'americano') {
    const c = cycle?.id ? cycle : latestCycle(item);
    return c?.id ? {type, id: c.id, name: `${item.name} / ${c.name}`} : null;
  }
  return {type, id: item.id, name: item.name ?? ''};
}

/** The competition charges now: an entry fee is set (a meet's price is `feeMinor`, or a membership's own price on it). */
export function chargesNow(kind, item) {
  if (!item) return false;
  return kind === 'meet' ? (item.feeMinor != null || isPaidRule(null, item.memberPrices)) : item.entryFeeMinor != null;
}

/**
 * The competition took money before (the club's entry-fee summaries: one row per source with any obligation):
 * a ladder or tournament by its id, a box league by the cycles' owning league, an americano league by its cycles.
 * Meets bill through their own history (the shell asks the meet's billing for that), so never here.
 */
export function chargedBefore(kind, item, summaries = []) {
  const id = item?.id;
  if (!id || !Array.isArray(summaries)) return false;
  switch (kind) {
    case 'ladder': return summaries.some(s => s.sourceType === 'LadderEntry' && s.sourceId === id);
    case 'tournament': return summaries.some(s => s.sourceType === 'TournamentEntry' && s.sourceId === id);
    case 'league': return summaries.some(s => s.sourceType === 'LeagueCycleEntry' && s.leagueId === id);
    case 'americano': {
      const cycleIds = new Set((item.cycles ?? []).map(c => c?.id).filter(Boolean));
      return summaries.some(s => s.sourceType === 'AmericanoCycleEntry' && cycleIds.has(s.sourceId));
    }
    default: return false;
  }
}

/**
 * A row "charges" when it carries a fee (X167: the API's feeMinor, every format) or when the club's entry-fee summaries
 * name it as having taken money before: a ladder or tournament by id, a box league by owning league, an americano league
 * by its current cycle (the row's cycleId). Meets never appear in the summaries; a paid meet is charged by its fee.
 */
export function rowCharged(row, summaries = []) {
  if (!row?.id) return false;
  // X167 (Grant): every paid competition offers Payments: the row carries its fee now (the current cycle's for leagues)
  if (row.feeMinor != null && Number(row.feeMinor) > 0) return true;
  if (!Array.isArray(summaries)) return false;
  switch (row.type) {
    case 'americano': return !!row.cycleId && summaries.some(s => s.sourceType === 'AmericanoCycleEntry' && s.sourceId === row.cycleId);
    default: return chargedBefore(row.type, {id: row.id}, summaries);
  }
}

/**
 * The ⋮ menu on a Competitions list row: Settings, Invite settings (in place, when the row has something to invite
 * to), Email players (meets have no mailer filter), Payments (when charged and the club's payments are on), and last
 * Delete (red; the old list pages' row action, back since X144 dropped it with the pages — the page opens
 * CompetitionDeleteDialog). Each item: {key, title, icon, to?, danger?} — the page handles `invite`, `email` and
 * `delete` itself.
 */
export function rowMenuItems(row, clubId, {charged = false, paymentsHidden = false} = {}) {
  if (!row?.id || !COMPETITION_KINDS.includes(row.type)) return [];
  const items = [{key: 'settings', title: 'Settings', icon: 'mdi-cog-outline', to: settingsRoute(row.type, clubId, row.id)}];
  if (row.joinReferenceId) items.push({key: 'invite', title: 'Invite settings', icon: 'mdi-link-variant'});
  if (emailFilters(row, clubId)) items.push({key: 'email', title: 'Email players', icon: 'mdi-email-outline'});
  if (charged && !paymentsHidden) items.push({key: 'payments', title: 'Payments', icon: 'mdi-credit-card-outline', to: paymentsRoute(row.type, clubId, row.id)});
  items.push({key: 'delete', title: 'Delete', icon: 'mdi-delete-outline', danger: true});
  return items;
}

/** The row a competition's shell shows in its chip: the list's status words (the API's status key). */
export function shellStatusChip(row) {
  return row?.status ? statusChip(row) : null;
}

/** The format's identity for the shell's title tile. */
export function shellIdentity(kind) {
  return {icon: competitionIcon(kind), name: formatExplainers[kind]?.singular ?? 'competition'};
}

/** Where the first tab is (the row's open target on the list). */
export function firstTabRoute(kind, clubId, id) {
  return competitionRoute({type: kind, id}, clubId);
}
